/**
 * Зведення двох джерел календаря.
 *
 * Змагання приходять із e-phan живцем, а CMS тримає два різні за роллю набори
 * записів (ADR-0012):
 *
 *   1. **власні заходи** — збори та професійне навчання, яких у джерелі немає взагалі.
 *      У них порожній `externalId`;
 *   2. **доповнення до змагань** — положення, партнери, організації, ознаки
 *      рейтингового та спартаківського заходу. Такий запис знаходить своє
 *      змагання за `externalId`.
 *
 * Чиє поле головніше: у доповненні перемагає заповнене значення з CMS, бо його
 * поставив редактор свідомо. Порожні поля доповнення нічого не затирають —
 * інакше створення доповнення заради одного лише положення стерло б із картки
 * місто й організатора.
 */

/** Порожнє значення, яке не має перекривати дані джерела. */
const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  value === false;

/** Доповнення поверх змагання. */
function extend(competition, supplement) {
  const merged = { ...competition };

  for (const [key, value] of Object.entries(supplement)) {
    if (isBlank(value)) continue;
    merged[key] = value;
  }

  // Ідентичність запису визначає джерело: адреса сторінки будується зі слага
  // змагання, а не зі слага доповнення, і позначка джерела лишається чесною.
  merged.id = competition.id;
  merged.slug = competition.slug;
  merged.source = 'ephan';
  merged.externalUrl = competition.externalUrl;

  return merged;
}

/**
 * @param {object[]} competitions заходи, зроблені зі змагань e-phan
 * @param {object[]} events записи CMS: власні заходи й доповнення
 */
export function mergeEvents(competitions = [], events = []) {
  const supplements = new Map();
  const hidden = new Set();
  const own = [];

  for (const event of events) {
    if (event.externalId) {
      // Доповнення з прапорцем «приховати» — це спосіб прибрати з календаря
      // змагання, яке лишається в джерелі. Іншого способу немає: чужий запис
      // ми не редагуємо.
      if (event.hidden) hidden.add(String(event.externalId));
      else supplements.set(String(event.externalId), event);
      continue;
    }

    if (!event.hidden) own.push(event);
  }

  const extended = competitions
    .filter((competition) => !hidden.has(String(competition.externalId)))
    .map((competition) => {
      const supplement = supplements.get(String(competition.externalId));
      return supplement ? extend(competition, supplement) : competition;
    });

  return [...extended, ...own].sort((a, b) => String(a.start).localeCompare(String(b.start)));
}
